import { Preferences } from '@capacitor/preferences';
import { CONSENT_STORAGE_KEY, CONSENT_VERSION } from '@/constants/consent';

let cache = null;
let initPromise = null;

function parseStoredState(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION || !parsed.acknowledged) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function readLegacyLocalStorage() {
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

async function persistState(payload) {
  cache = payload;
  const serialized = JSON.stringify(payload);
  await Preferences.set({ key: CONSENT_STORAGE_KEY, value: serialized });
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, serialized);
  } catch {
    // Optional mirror for web tooling.
  }
}

export async function initConsentStorage() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { value } = await Preferences.get({ key: CONSENT_STORAGE_KEY });
      cache = parseStoredState(value);
      if (cache) return;

      const legacy = readLegacyLocalStorage();
      cache = parseStoredState(legacy);
      if (cache) {
        await persistState(cache);
      }
    } catch {
      cache = parseStoredState(readLegacyLocalStorage());
    }
  })();

  return initPromise;
}

export function loadConsentState() {
  return cache;
}

export async function saveAcknowledgedConsent() {
  const payload = {
    version: CONSENT_VERSION,
    acknowledged: true,
    decidedAt: new Date().toISOString(),
    categories: {
      necessary: true,
      analytics: false,
    },
  };
  await persistState(payload);
  return payload;
}

export async function clearConsentState() {
  cache = null;
  await Preferences.remove({ key: CONSENT_STORAGE_KEY });
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
}
